// From https://github.com/benfred/venn.js

// Finds the zeros of a function
// given two starting points
export function bisect(f, a, b) {
    const maxIterations = 100;
    const tolerance = 1e-10;
    const fA = f(a);
    const fB = f(b);
    let delta = b - a;

    if (fA === 0) return a;
    if (fB === 0) return b;

    for (var i = 0; i < maxIterations; ++i) {
        delta /= 2;
        var mid = a + delta,
            fMid = f(mid);

        if (fMid * fA >= 0) {
            a = mid;
        }

        if (Math.abs(delta) < tolerance || fMid === 0) {
            return mid;
        }
    }
    return a + delta;
}

// Returns the overlap area of two circles of radius r1 and r2
// that have their centers separated by distance d
export function circleOverlapArea(r1, r2, d) {
    // No overlap
    if (d >= r1 + r2) {
        return 0;
    }

    // Completely overlapped
    if (d <= Math.abs(r1 - r2)) {
        return Math.PI * Math.min(r1, r2) * Math.min(r1, r2);
    }

    const w1 = r1 - (d * d - r2 * r2 + r1 * r1) / (2 * d);
    const w2 = r2 - (d * d - r1 * r1 + r2 * r2) / (2 * d);

    return circleSegmentArea(r1, w1) + circleSegmentArea(r2, w2);
}

// Circular segment area calculation
// See http://mathworld.wolfram.com/CircularSegment.html
function circleSegmentArea(r, width) {
    return r * r * Math.acos(1 - width / r) - (r - width) * Math.sqrt(width * (2 * r - width));
}

export default class Venn {
    constructor(aSetCount, bSetCount, uSetCount, scale = 1) {
        this.aSetCount = aSetCount;
        this.bSetCount = bSetCount;
        this.uSetCount = uSetCount;
        this.scale = scale;
    }

    _setArea(set) {
        let count = set + "SetCount";
        return this[count] * this.scale;
    }

    get aSetArea() {
        return this._setArea("a");
    }

    get bSetArea() {
        return this._setArea("b");
    }

    get uSetArea() {
        return this._setArea("u");
    }

    _setRadius(set) {
        let area = set + "SetArea";
        return Math.sqrt(this[area] / Math.PI);
    }

    get aSetRadius() {
        return this._setRadius("a");
    }

    get bSetRadius() {
        return this._setRadius("b");
    }

    _setDiameter(set) {
        let r = set + "SetRadius";
        return this[r] * 2;
    }

    get aSetDiameter() {
        return this._setDiameter("a");
    }

    get bSetDiameter() {
        return this._setDiameter("b");
    }

    get setDistance() {
        let r1 = this.aSetRadius;
        let r2 = this.bSetRadius;
        let uArea = this.uSetArea;

        let overlapping = Math.min(r1, r2) * Math.min(r1, r2) * Math.PI <= uArea + 1e-10;

        if (overlapping) {
            return Math.abs(r1 - r2);
        }

        // We loop on an ever-decreasing distance between sets
        // until we have an approximate overlap area that matches the count
        return bisect(dist => circleOverlapArea(r1, r2, dist) - uArea, 0, r1 + r2);
    }

    get aSetIntersectDist() {
        let r1 = this.aSetRadius;
        let r2 = this.bSetRadius;
        let d = this.setDistance;

        if (d <= 1e-10) {
            return d;
        }

        // See http://mathworld.wolfram.com/Circle-CircleIntersection.html
        let num = d * d - r2 * r2 + r1 * r1;
        let den = 2 * d;
        return num / den;
    }

    get bSetIntersectDist() {
        return this.setDistance - this.aSetIntersectDist;
    }
}
